import { CostPriceIngredient } from '../../../../services/costPrice/costPriceService';
import { useCostPriceStore } from '../../../../stores/appCostPriceStore';
import { ReviewIngredient } from '../../../../types/reviewTypes';
import { resolveProductIdentifier } from '../../../../utils/platformUtils';
import { candidateUnit, MatchingIngredientRef, SwapReviewAdapter } from '../review/reviewAdapter';
import { resolveRowIndex, writeDishIngredients } from './automationRunWrites';

// Include the source restaurant: the same supplier article in two restaurants
// does not identify the same recipe reference. Sentinel article IDs use names.
export function sameOrderedProduct(a: CostPriceIngredient, b: CostPriceIngredient, restaurantId: string): boolean {
  const identity = (row: CostPriceIngredient) => row.platform && !row.custom && !row.subrecipeId && !row.nestedCostPriceId
    ? resolveProductIdentifier(row.platform.toLowerCase(), row.articleId, row.productName) : undefined;
  const aId = identity(a);
  return !!aId && aId === identity(b) && a.platform?.toLowerCase() === b.platform?.toLowerCase()
    && (a.sourceRestaurantId || restaurantId) === (b.sourceRestaurantId || restaurantId);
}

function sameSwapIngredient(a: CostPriceIngredient, b: CostPriceIngredient, restaurantId: string): boolean {
  if (!a.custom && !b.custom) return sameOrderedProduct(a, b, restaurantId);
  return !!a.custom && !!b.custom && !a.subrecipeId && !b.subrecipeId && !a.nestedCostPriceId && !b.nestedCostPriceId
    && !!a.articleId && a.articleId === b.articleId && a.platform === b.platform
    && (a.sourceRestaurantId || restaurantId) === (b.sourceRestaurantId || restaurantId);
}

export function createAutomationRunSwap(
  restaurantId: string, runId: string, original: CostPriceIngredient,
  write: (dishId: string, operation: () => Promise<void>) => Promise<void>,
) {
  const snapshots = new WeakMap<ReviewIngredient, CostPriceIngredient>();
  const reviewRow = (row: CostPriceIngredient): ReviewIngredient => {
    const ingredient: ReviewIngredient = {
      templateName: row.productName ?? '', templateQuantity: row.quantityNeeded ?? 0,
      templateUnit: row.quantityInfo?.stdUnit ?? '', status: 'matched', alternatives: [],
      productName: row.productName, articleId: row.articleId, platform: row.platform,
      quantityNeeded: row.quantityNeeded, quantityInfo: row.quantityInfo,
      isCustom: row.custom, customPlatformName: row.customPlatformName,
    };
    snapshots.set(ingredient, row);
    return ingredient;
  };
  const ingredient = reviewRow(original);
  const adapter: SwapReviewAdapter = {
    allowedSwapKinds: original.custom ? ['product', 'custom', 'subrecipe'] : ['product'],
    findSameIngredientElsewhere(_name, sourceDishId, candidate) {
      const matches: MatchingIngredientRef[] = [];
      useCostPriceStore.getState().costPriceList.forEach(dish => {
        if (dish.restaurantId !== restaurantId || dish.automationRunId !== runId || dish.id === sourceDishId) return;
        dish.ingredients.forEach((row, index) => {
          if (!sameSwapIngredient(original, row, restaurantId)) return;
          const target = { ...candidate, custom: candidate.kind === 'custom', productName: candidate.displayName, id: '' };
          if (sameSwapIngredient(row, target, restaurantId)) return;
          const sameUnit = !!row.quantityInfo?.stdUnit && row.quantityInfo.stdUnit === candidateUnit(candidate);
          matches.push({ itemId: dish.id, itemName: dish.costPriceName, ingredientIndex: index,
            ingredient: reviewRow(row), currentMatchName: row.productName,
            quantity: sameUnit ? row.quantityNeeded ?? 0 : 0, requiresQuantity: !sameUnit || !(row.quantityNeeded! > 0) });
        });
      });
      return matches;
    },
    async swap(dishId, reference, _index, candidate, quantity) {
      const validTarget = candidate.kind === 'subrecipe' ? !!candidate.subrecipeId && !!candidate.portionsUnit
        : !!candidate.platform && !!candidate.quantityInfo;
      if (!adapter.allowedSwapKinds.includes(candidate.kind) || !validTarget || !Number.isFinite(quantity) || quantity <= 0) {
        throw new Error('Invalid replacement');
      }
      const snapshot = snapshots.get(reference);
      if (!snapshot) throw new Error('Ingredient no longer available');
      await write(dishId, async () => {
        await writeDishIngredients(dishId, dish => {
          const store = useCostPriceStore.getState();
          if (store.currentRestaurantId !== restaurantId || dish.restaurantId !== restaurantId || dish.automationRunId !== runId) {
            throw new Error('Review run no longer available');
          }
          const index = resolveRowIndex(dish, snapshot.id);
          const row = dish.ingredients[index];
          if (!row || !sameSwapIngredient(snapshot, row, restaurantId)
            || row.quantityNeeded !== snapshot.quantityNeeded || row.quantityInfo?.stdUnit !== snapshot.quantityInfo?.stdUnit) {
            throw new Error('Ingredient changed; reopen the replacement picker');
          }
          // Replacement products come from this restaurant's catalogue. Drop the
          // previous source and live-price fields, retaining recipe-specific waste
          // and purchasing alternatives, except the new primary itself.
          const replacement: CostPriceIngredient = candidate.kind === 'subrecipe' ? {
            id: row.id, subrecipeId: candidate.subrecipeId, subrecipePortionsNeeded: quantity, wastePercentage: row.wastePercentage,
            automationTemplateKey: row.automationTemplateKey,
          } : {
            id: row.id, articleId: candidate.articleId, productName: candidate.displayName,
            automationTemplateKey: row.automationTemplateKey,
            platform: candidate.platform, customPlatformName: candidate.customPlatformName,
            custom: candidate.kind === 'custom' ? true : undefined,
            quantityInfo: candidate.quantityInfo, quantityNeeded: quantity,
            wastePercentage: row.wastePercentage,
            alternatives: candidate.kind === 'custom' || (row.sourceRestaurantId && row.sourceRestaurantId !== restaurantId) ? undefined
              : row.alternatives?.filter(alt => !sameOrderedProduct(alt, { id: '', productName: candidate.displayName, ...candidate }, restaurantId)),
          };
          return dish.ingredients.map((item, i) => i === index ? replacement : item);
        });
      });
    },
  };
  return { ingredient, adapter };
}
